import { consumeHandoffToken } from './consumeHandoffToken';

export interface CreateHandoffRouteHandlerOptions {
  /** Destination's own PHP backend base URL (server env var). */
  backendUrl: string;
  /** Destination's service API key (server env var). */
  serviceApiKey: string;
  /** Internal shared secret between this Next server and its PHP backend. */
  internalToken: string;
  /** Where to send users on missing/invalid tokens. Default: `/login`. */
  loginPath?: string;
  /** Landing page path that runs the client-side multi-account auto-add. Default: `/auth/handoff/landing`. */
  landingPath?: string;
}

/**
 * Build a Next App Router GET handler for `app/auth/handoff/route.ts`.
 *
 * Consumer usage (copy-pasta — package can't ship route files):
 *
 * ```ts
 * // src/app/auth/handoff/route.ts
 * import { createHandoffRouteHandler } from '@benbraide/auth-service-nextjs/sharing';
 *
 * export const GET = createHandoffRouteHandler({
 *   backendUrl: process.env.NEXT_PUBLIC_BACKEND_URL!,
 *   serviceApiKey: process.env.NEXT_SERVICE_API_KEY!,
 *   internalToken: process.env.NEXT_INTERNAL_TOKEN!,
 * });
 * ```
 */
export function createHandoffRouteHandler(
  options: CreateHandoffRouteHandlerOptions,
) {
  const loginPath = options.loginPath ?? '/login';
  const landingPath = options.landingPath ?? '/auth/handoff/landing';

  return async function GET(request: Request): Promise<Response> {
    const url = new URL(request.url);
    const token = url.searchParams.get('token');
    const nextParam = url.searchParams.get('next');

    if (!token) {
      return Response.redirect(new URL(loginPath, url), 302);
    }

    const result = await consumeHandoffToken({
      token,
      backendUrl: options.backendUrl,
      serviceApiKey: options.serviceApiKey,
      internalToken: options.internalToken,
    });

    if (!result.ok) {
      const errUrl = new URL(loginPath, url);
      errUrl.searchParams.set('error', `handoff_${result.reason}`);
      return Response.redirect(errUrl, 302);
    }

    const bridge = new URL(landingPath, url);
    bridge.searchParams.set('account_uuid', result.user.uuid);
    bridge.searchParams.set('session_token', result.sessionToken);
    bridge.searchParams.set('next', nextParam ?? result.nextPath);
    bridge.searchParams.set('toast', `Now viewing as ${result.user.name}`);
    return Response.redirect(bridge, 302);
  };
}
